import { useChatStore } from '@/store/chatStore';
import { useCallback, useEffect, useRef } from 'react';

interface UseInactivityTimerOptions {
  enabled: boolean;
  timeoutMs: number;
  warningLeadMs: number;
  isOpen: boolean;
  debounceMs?: number; // Optional debounce delay, defaults to 300ms
}

/**
 * Custom hook to handle inactivity timers for chat widget
 * Manages warning and reset timers based on user activity
 * Uses debounce to prevent excessive timer resets during active user interaction
 */
export const useInactivityTimer = ({
  enabled,
  timeoutMs,
  warningLeadMs,
  isOpen,
  debounceMs = 300,
}: UseInactivityTimerOptions) => {
  const { resetSession, setShowInactivityWarning, setShowInactivityReset } = useChatStore();

  const warningTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);
  const resetTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isOpenRef = useRef(isOpen);
  const timeoutMsRef = useRef(timeoutMs);
  const warningLeadMsRef = useRef(warningLeadMs);
  const debounceMsRef = useRef(debounceMs);

  // Keep refs in sync with props
  useEffect(() => {
    isOpenRef.current = isOpen;
    timeoutMsRef.current = timeoutMs;
    warningLeadMsRef.current = warningLeadMs;
    debounceMsRef.current = debounceMs;
  }, [isOpen, timeoutMs, warningLeadMs, debounceMs]);

  const clearTimers = useCallback(() => {
    if (warningTimeoutRef.current) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    if (debounceTimeoutRef.current) {
      window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();
    lastActivityRef.current = Date.now();

    const warningLeadTimeMs = Math.min(timeoutMsRef.current, Math.max(0, warningLeadMsRef.current));
    const warningDelay = warningLeadTimeMs;

    warningTimeoutRef.current = window.setTimeout(() => {
      setShowInactivityWarning(true);
    }, warningDelay);

    resetTimeoutRef.current = window.setTimeout(() => {
      clearTimers();
      setShowInactivityWarning(false);
      resetSession();
      setShowInactivityReset(true);
    }, timeoutMsRef.current);

    console.log('warningDelay', warningDelay);
    console.log('resetTimeout', timeoutMsRef.current);
  }, [clearTimers, resetSession, setShowInactivityWarning, setShowInactivityReset]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!enabled || timeoutMs <= 0) {
      clearTimers();
      setShowInactivityWarning(false);
      setShowInactivityReset(false);
      return () => {
        clearTimers();
      };
    }

    if (!isOpen) {
      clearTimers();
      setShowInactivityWarning(false);
      setShowInactivityReset(false);
      return () => {
        clearTimers();
      };
    }

    // Start timers immediately on mount
    startTimers();

    const activityEvents: (keyof WindowEventMap)[] = [
      'pointerdown',
      'pointermove',
      'keydown',
      'touchstart',
      'scroll',
      'focus',
    ];

    // Debounced function to restart timers after user stops being active
    const debouncedRestartTimers = () => {
      if (!isOpenRef.current) return;
      setShowInactivityWarning(false);
      setShowInactivityReset(false);
      startTimers();
    };

    const handleActivity = () => {
      if (!isOpenRef.current) return;

      const now = Date.now();
      lastActivityRef.current = now;

      // Clear existing debounce timer
      if (debounceTimeoutRef.current) {
        window.clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }

      // Set new debounce timer - restart timers only after user stops being active
      debounceTimeoutRef.current = window.setTimeout(() => {
        debounceTimeoutRef.current = null;
        debouncedRestartTimers();
      }, debounceMsRef.current);
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // On visibility change, restart timers immediately without debounce
        if (debounceTimeoutRef.current) {
          window.clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        setShowInactivityWarning(false);
        setShowInactivityReset(false);
        startTimers();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimers();
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    enabled,
    timeoutMs,
    isOpen,
    clearTimers,
    startTimers,
    setShowInactivityWarning,
    setShowInactivityReset,
  ]);
};
